// app/components/ErrorFallback.tsx
import { useEffect, useRef } from "react";
import { useRouteError, Link, isRouteErrorResponse } from "react-router";
import { Button } from "./ui/button";
import { captureException } from "~/lib/analytics";

interface Props {
  section?: string;
}

export function ErrorFallback({ section }: Props) {
  const error = useRouteError();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
    ? error.message
    : "Unknown error";

  // Phase 4c: ship error to PostHog. captureException no-ops when PostHog
  // isn't initialized (dev without VITE_POSTHOG_KEY) so this stays safe.
  useEffect(() => {
    captureException(error, { boundary: "route", section });
  }, [error, section]);

  // Move focus to the heading when the boundary mounts so keyboard / screen-reader
  // users land on the error context instead of a now-detached element.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="flex flex-col items-center justify-center p-12 text-center"
    >
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-2xl font-semibold mb-2 outline-none"
      >
        Something went wrong{section ? ` in ${section}` : ""}.
      </h2>
      <p className="text-muted-foreground mb-6 max-w-md">
        We've logged the error. Try reloading or returning home.
      </p>
      <details className="mb-6 max-w-md">
        <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
          Error details
        </summary>
        <pre className="mt-2 text-xs text-left bg-muted p-3 rounded overflow-auto max-h-48">
          {message}
        </pre>
      </details>
      <div className="flex gap-3">
        <Button onClick={() => window.location.reload()}>Try again</Button>
        <Button variant="ghost" asChild>
          <Link to="/">Go home</Link>
        </Button>
      </div>
    </div>
  );
}
